import axios from "axios";
import type { ReadRequest } from "./policies";

/**
 * Batch per-item reads, so a `useQuery` per row does not bring back the N+1
 * (Sentry NEXT-GEN-FRONTEND-2B).
 *
 * Each row keeps its own query and its own key, so it caches, invalidates and
 * reports errors on its own. Its query function asks the batcher for its one
 * item. The batcher collects every item asked for together, sends them in as
 * few requests as the endpoint allows, and gives each caller its own answer:
 *
 *     const verdicts = createBatcher({
 *       name: "permissions",
 *       idOf: checkId,
 *       groupOf: (check) => check.tenant,
 *       maxBatch: MAX_CHECKS_PER_REQUEST,
 *       fetchBatch: (checks, request) => service.can(checks[0].tenant, checks, request).then(byId),
 *     });
 *     // in the query definition
 *     queryFn: scopedRead((request) => verdicts.load(check, request)),
 *
 * - **One tick.** Loads are collected until the current microtask queue
 *   drains, which is when every `useQuery` mounted in one render has started.
 *   A load after that starts the next batch.
 * - **Or one window**, for a batcher whose rows refresh on an interval
 *   (`collectMs`). Each row's `refetchInterval` fires in a task of its own, so
 *   a microtask flush sends every row's refresh alone: a page of N rows sent N
 *   requests a minute (NG-3318). Collecting for {@link ROW_REFRESH_WINDOW_MS}
 *   from the first load lets the rows' timers, which fire within a few ms of
 *   each other, share one request again. They stay that close: Query restarts
 *   an observer's interval each time its query updates, so rows answered by
 *   one request restart together. The window is fixed from the first load,
 *   never extended, so no read waits more than `collectMs` for its request.
 * - **Same item, one slot.** Two loads of one id in a tick are sent once and
 *   both get the answer.
 * - **Groups never share a request.** Items whose `groupOf` differ (another
 *   workspace, another project) go in separate requests.
 * - **Chunked.** At most `maxBatch` items per request; the chunks go out
 *   together.
 * - **Failure.** A request that fails rejects every load it carried. An item
 *   the answer leaves out rejects that load alone ({@link BatchItemMissingError}).
 * - **Cancellation.** A load whose signal aborts rejects with a cancel at once.
 *   The request it rode on is aborted only when every load on it has aborted,
 *   since the others still want the answer.
 *
 * Retries stay with each load's own `scopedRead`/`runRead` (contract §6.2):
 * the batcher never retries, and loads that retry in the same tick (or window)
 * share a request again.
 */
export interface BatcherOptions<TItem, TResult> {
  /** Names the batcher in errors and logs. */
  readonly name: string;
  /** The item's identity. Loads with the same id in a tick share one slot. */
  readonly idOf: (item: TItem) => string;
  /** Items in different groups are never sent together. Defaults to one group. */
  readonly groupOf?: (item: TItem) => string;
  /** The most items one request may carry. */
  readonly maxBatch: number;
  /**
   * Collect loads for this many ms from the first, instead of until the
   * microtask queue drains. Only for rows refreshed on an interval: anything
   * gating a first paint (permission verdicts) keeps the microtask flush.
   */
  readonly collectMs?: number;
  /**
   * One request for `items` (all in one group, at most `maxBatch`), answering
   * by `idOf`. `request` carries the abort signal and the timeout for the
   * whole request.
   */
  readonly fetchBatch: (
    items: readonly TItem[],
    request: ReadRequest,
  ) => Promise<ReadonlyMap<string, TResult>>;
}

export interface Batcher<TItem, TResult> {
  /** Read one item; it goes out with every other item asked for in this tick (or window). */
  load(item: TItem, request: ReadRequest): Promise<TResult>;
}

/**
 * The window for rows that refresh on an interval: wide enough for their
 * timers, which fire within a few ms of each other, to land in it even with a
 * long frame between them, and short next to the request it delays.
 */
export const ROW_REFRESH_WINDOW_MS = 25;

/** A batched request answered, but said nothing about this item. */
export class BatchItemMissingError extends Error {
  constructor(batcher: string, id: string) {
    super(`${batcher}: the batched answer had no entry for ${id}`);
    this.name = "BatchItemMissingError";
  }
}

interface Waiter<TResult> {
  readonly request: ReadRequest;
  resolve(value: TResult): void;
  reject(error: unknown): void;
  /** Set once the caller has stopped waiting. */
  done: boolean;
  /** Told when this waiter aborts, once it has been sent. */
  onAbort: (() => void) | null;
}

interface Slot<TItem, TResult> {
  readonly item: TItem;
  readonly waiters: Waiter<TResult>[];
}

export function createBatcher<TItem, TResult>(
  options: BatcherOptions<TItem, TResult>,
): Batcher<TItem, TResult> {
  const { name, idOf, groupOf = () => "", maxBatch, collectMs, fetchBatch } = options;
  if (!Number.isInteger(maxBatch) || maxBatch < 1) {
    throw new Error(`${name}: maxBatch must be a positive integer, got ${maxBatch}`);
  }
  if (collectMs !== undefined && !(collectMs > 0)) {
    throw new Error(`${name}: collectMs must be positive, got ${collectMs}`);
  }
  /** This tick's (or window's) loads: group → id → slot, in the order first asked. */
  let pending = new Map<string, Map<string, Slot<TItem, TResult>>>();
  let flushQueued = false;

  function flush(): void {
    const groups = pending;
    pending = new Map();
    flushQueued = false;
    for (const slots of groups.values()) {
      // A slot every caller already left is not sent.
      const live = [...slots].filter(([, slot]) => slot.waiters.some((waiter) => !waiter.done));
      for (let start = 0; start < live.length; start += maxBatch) {
        send(live.slice(start, start + maxBatch));
      }
    }
  }

  function send(chunk: [string, Slot<TItem, TResult>][]): void {
    const waiters = chunk.flatMap(([, slot]) => slot.waiters);
    const controller = new AbortController();
    const abortIfAbandoned = () => {
      if (waiters.every((waiter) => waiter.done)) controller.abort();
    };
    for (const waiter of waiters) waiter.onAbort = abortIfAbandoned;
    // The request lives as long as its most patient caller is willing to wait.
    const timeout = Math.max(...waiters.map((waiter) => waiter.request.timeout));

    let answer: Promise<ReadonlyMap<string, TResult>>;
    try {
      answer = fetchBatch(
        chunk.map(([, slot]) => slot.item),
        { signal: controller.signal, timeout, transientRetry: false },
      );
    } catch (error) {
      answer = Promise.reject(error);
    }
    answer.then(
      (results) => {
        for (const [id, slot] of chunk) {
          if (results.has(id)) {
            const value = results.get(id) as TResult;
            for (const waiter of slot.waiters) settle(waiter, () => waiter.resolve(value));
          } else {
            const missing = new BatchItemMissingError(name, id);
            for (const waiter of slot.waiters) settle(waiter, () => waiter.reject(missing));
          }
        }
      },
      (error: unknown) => {
        // Each caller's query logs its own failure (`client.ts`); the batcher
        // only hands the error on.
        for (const waiter of waiters) settle(waiter, () => waiter.reject(error));
      },
    );
  }

  function settle(waiter: Waiter<TResult>, finish: () => void): void {
    if (waiter.done) return;
    waiter.done = true;
    finish();
  }

  return {
    load(item, request) {
      return new Promise<TResult>((resolve, reject) => {
        const waiter: Waiter<TResult> = {
          request,
          resolve: (value) => {
            request.signal.removeEventListener("abort", cancel);
            resolve(value);
          },
          reject: (error) => {
            request.signal.removeEventListener("abort", cancel);
            reject(error);
          },
          done: false,
          onAbort: null,
        };
        function cancel() {
          settle(waiter, () => waiter.reject(new axios.CanceledError()));
          waiter.onAbort?.();
        }
        if (request.signal.aborted) {
          cancel();
          return;
        }
        request.signal.addEventListener("abort", cancel, { once: true });

        const group = groupOf(item);
        const id = idOf(item);
        let slots = pending.get(group);
        if (!slots) {
          slots = new Map();
          pending.set(group, slots);
        }
        let slot = slots.get(id);
        if (!slot) {
          slot = { item, waiters: [] };
          slots.set(id, slot);
        }
        slot.waiters.push(waiter);

        if (!flushQueued) {
          flushQueued = true;
          if (collectMs === undefined) queueMicrotask(flush);
          else setTimeout(flush, collectMs);
        }
      });
    },
  };
}
